<template>
  <SettingsPagePanel>
    <SettingsPagePanelHeader>
      <template #title>予約詳細</template>
      <template #right v-if="canEditCalendarSetting">
        <FlatButton
          :class="$style.cancelButton"
          variant="caution"
          :disabled="!!booking?.cancelReason"
          @click="openCancelBookingModal"
        >
          キャンセル
        </FlatButton>
      </template>
    </SettingsPagePanelHeader>
    <div v-if="booking" :class="$style.container">
      <div :class="$style.summary">
        <DolphinChip
          v-if="bookingStatus"
          :class="[$style.statusChip, $style[`statusChip--${bookingStatus}`]]"
        >
          {{ bookingStatusString[bookingStatus] }}
        </DolphinChip>
        <h2>{{ booking.summary }}</h2>
      </div>
      <div :class="$style.plan">
        {{ dateStringForDisplay(booking) }}
        <div v-if="booking.cancelReason">
          {{ cancelReasonString[booking.cancelReason] }}
        </div>
      </div>
      <div>
        <span :class="$style.label">担当メンバー: </span>
        <router-link
          v-if="booking.assignedStaff.guid"
          :to="{
            name: 'memberManagementSettingDetail',
            params: {
              memberManagementSettingId: booking.assignedStaff.guid,
              entranceGUID: route.params.entranceGUID,
            },
          }"
        >
          {{ booking.assignedStaff.displayName }}
        </router-link>
      </div>
      <div v-if="booking.description" :class="$style.description">
        <span :class="$style.label">詳細: </span>
        <div :class="$style.description__inner">
          {{ booking.description }}
        </div>
      </div>
      <div>
        <span :class="$style.label">Web会議URL: </span>
        <ExternalLink
          v-if="booking.meetingURL"
          :href="booking.meetingURL"
          :class="$style.externalLink"
        >
          {{ booking.meetingURL }} </ExternalLink
        ><span v-else>なし</span>
      </div>
      <div :class="$style.userInfo">
        <span :class="$style.label"
          >オカネコユーザー: {{ booking.okanekoUser ? '◯' : '×' }}</span
        >
        <div v-if="booking.okanekoUser" :class="$style.userInfo__inner">
          <p>ユーザーID: {{ booking.okanekoUser.id }}</p>
          <p>ニックネーム: {{ booking.okanekoUser.nickname }}</p>
          <p>
            チャンネル: <ChannelChip :channel="booking.okanekoUser.channel" />
          </p>
          <p>
            カルテ:
            <router-link
              v-if="booking.okanekoUser.karte?.id"
              :to="{
                name: 'karteDetail',
                params: {
                  karteId: booking.okanekoUser.karte?.id,
                  entranceGUID: route.params.entranceGUID,
                },
              }"
              >あり</router-link
            >
            <span v-else>なし</span>
          </p>
        </div>
      </div>
      <CalendarBookingViewQuestionAnswers
        v-if="booking.questionAnswers"
        :question-answers="booking.questionAnswers"
      />
      <div :class="$style.additionalInfo">
        予約ID: {{ booking.guid }}<br />
        ユーザーID: {{ booking.userGUID }}
      </div>
    </div>
    <CancelBookingModal>
      <div :class="$style.cancelBookingModalContainer">
        <h3>キャンセル理由をお選びください。</h3>
        <DolphinSelector
          v-model="selectedCancelReason"
          :options="cancelBookingOptions"
          placeholder="選択してください"
        />
        <div :class="$style.note">
          ※キャンセルすると、連携している外部カレンダーの予定は削除されます。
        </div>
        <div :class="$style.buttons">
          <FlatButton variant="secondary" @click="closeCancelBookingModal">
            閉じる
          </FlatButton>
          <FlatButton
            :disabled="!selectedCancelReason || isSubmitting"
            @click="handleSubmit"
          >
            送信する
          </FlatButton>
        </div>
      </div>
    </CancelBookingModal>
  </SettingsPagePanel>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { BookingCancelReason } from '@/types/schema.generated'
import ChannelChip from '@/components/atoms/ChannelChip.vue'
import DolphinChip from '@/components/atoms/DolphinChip.vue'
import DolphinSelector from '@/components/atoms/DolphinSelector.vue'
import FlatButton from '@/components/atoms/FlatButton.vue'
import ExternalLink from '@/components/atoms/ExternalLink.vue'
import { useModal } from '@/hooks/useModal'
import { useAlert } from '@/libs/alertPortal'
import { wait } from '@/libs/utilities/wait'
import {
  useFetchBookingQuery,
  useCancelBookingMutation,
} from './SettingsPageCalendarBookingView.generated.ts'
import { useCalendarSettingsPageQuery } from './SettingsPageCalendarView.generated.ts'
import SettingsPagePanelHeader from '../SettingsPagePanelHeader.vue'
import SettingsPagePanel from '../SettingsPagePanel.vue'
import { cancelReasonString, dateStringForDisplay } from './booking.ts'
import { useStaff } from '@/hooks/useStaff.ts'
import CalendarBookingViewQuestionAnswers from './questions/CalendarBookingViewQuestionAnswers/CalendarBookingViewQuestionAnswers.vue'

const route = useRoute('calendarSettingBooking')
const {
  refetch: calendarBookingQueryRefetch,
  result: calendarBookingQueryResult,
} = useFetchBookingQuery(
  {
    guid: route.params.bookingGUID as string,
  },
  { fetchPolicy: 'no-cache' }
)

const booking = computed(() => calendarBookingQueryResult?.value?.booking)

type BookingStatus = 'open' | 'canceled'

const bookingStatus = computed((): BookingStatus | null => {
  if (!booking.value) {
    return null
  }

  if (booking.value.cancelReason) {
    return 'canceled'
  }

  if (new Date(booking.value.startsAt) > new Date()) {
    return 'open'
  }

  return null
})

const bookingStatusString: Record<BookingStatus, string> = {
  open: '開催前',
  canceled: 'キャンセル済み',
}

const alert = useAlert()

const { openCancelBookingModal, closeCancelBookingModal, CancelBookingModal } =
  useModal('CancelBooking')

const cancelBookingOptions = [
  {
    text: 'プランナーによるキャンセル',
    value: BookingCancelReason.EntranceCancelled,
  },
  {
    text: 'ユーザーによるキャンセル',
    value: BookingCancelReason.UserCancelled,
  },
]

const selectedCancelReason = ref<BookingCancelReason | null>(null)

const isSubmitting = ref(false)

const { mutate: cancelBookingMutate } = useCancelBookingMutation()

const handleSubmit = async () => {
  if (!selectedCancelReason.value) {
    return
  }

  isSubmitting.value = true

  try {
    cancelBookingMutate({
      guid: route.params.bookingGUID as string,
      cancelReason: selectedCancelReason.value,
    })
    closeCancelBookingModal()
    alert.success('キャンセルしました')
    await wait(1000)
    calendarBookingQueryRefetch()
  } catch (e) {
    console.error(e)
    alert.error('エラーが発生しました。再度お試しください。')
  } finally {
    isSubmitting.value = false
  }
}

const { currentStaff } = useStaff()

const { result: calendarSettingsPageQueryResult } =
  useCalendarSettingsPageQuery({ fetchPolicy: 'cache-first' })
const canEditCalendarSetting = computed(() => {
  if (!currentStaff.value) {
    return false
  }

  if (!calendarSettingsPageQueryResult.value) {
    return false
  }

  return (
    calendarSettingsPageQueryResult.value.uiControl
      .editOkanekoCalendarSetting ||
    currentStaff.value?.guid === booking.value?.assignedStaff.guid
  )
})
</script>

<style lang="scss" module>
.container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  font-weight: bold;
}

.summary {
  display: flex;
  flex-direction: row;
  gap: 8px;
}

.statusChip {
  color: $color-text-and-object-high-emphasis-inverse;

  &--open {
    background-color: $color-label-green;
  }

  &--canceled {
    background-color: $color-label-gray;
  }
}

.plan {
  display: flex;
  flex-direction: row;
  gap: 16px;
  font-weight: bold;
}

.description {
  &__inner {
    white-space: pre-wrap;
    padding: 8px;
    background-color: $color-surface-secondary;
    border-radius: $border-radius-inner;
  }
}

.externalLink {
  color: $color-text-and-object-accent-primary;

  &:hover {
    color: $color-text-and-object-accent-primary;
  }
}

.userInfo {
  &__inner {
    padding: 8px;
    background-color: $color-surface-secondary;
    border-radius: $border-radius-inner;
  }
}

.cancelButton {
  max-width: 70px;
}

.additionalInfo {
  margin-top: 16px;
  color: $color-text-and-object-low-emphasis;
  font-size: 10px;
}

.cancelBookingModalContainer {
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.note {
  color: $color-text-and-object-accent-red;
}

.buttons {
  display: flex;
  flex-direction: row;
  gap: 12px;
  justify-content: flex-end;
}
</style>
